<template>
    <TransitionRoot appear :show="isOpen" as="template">
        <Dialog as="div" class="relative z-50" :static="true">
            <TransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black/25" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div
                    class="flex items-center justify-center min-h-full p-4 text-center"
                >
                    <TransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 scale-95"
                        enter-to="opacity-100 scale-100"
                        leave="duration-200 ease-in"
                        leave-from="opacity-100 scale-100"
                        leave-to="opacity-0 scale-95"
                    >
                        <DialogPanel
                            class="w-full max-w-6xl p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl dark:bg-gray-800 rounded-2xl"
                        >
                            <DialogTitle
                                as="h3"
                                class="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                            >
                                Upload de Documentos Pendentes
                            </DialogTitle>
                            
                            <div 
                                v-if="loadingDocuments"
                                class="flex items-center justify-center py-8"
                            >
                                <svg
                                    aria-hidden="true"
                                    class="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                                    viewBox="0 0 100 101"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                        fill="currentFill"
                                    />
                                </svg>
                                <span class="text-gray-600 dark:text-gray-400">Carregando documentos...</span>
                            </div>

                            <div v-else-if="pendingDocuments.length === 0" class="py-8 text-center">
                                <p class="text-gray-600 dark:text-gray-400">
                                    Todos os documentos já foram enviados!
                                </p>
                            </div>

                            <div v-else class="mt-4 space-y-6 max-h-[70vh] overflow-y-auto">
                                <div
                                    v-for="docType in pendingDocuments"
                                    :key="docType.value"
                                    class="p-4 border border-gray-300 rounded-lg dark:border-gray-600"
                                >
                                    <h4 class="mb-3 text-base font-semibold text-gray-900 dark:text-white">
                                        {{ docType.label }}
                                    </h4>

                                    <div
                                        v-if="
                                            docType.value === 'document_front' ||
                                            docType.value === 'document_back'
                                        "
                                        class="grid grid-cols-2 gap-3 mb-3"
                                    >
                                        <div>
                                            <label class="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-300">
                                                Tipo de Identificação
                                            </label>
                                            <select
                                                v-model="documentData[docType.value].identificationType"
                                                class="block w-full p-2 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                            >
                                                <option value="">Selecione</option>
                                                <option value="rg">RG</option>
                                                <option value="cnh">CNH</option>
                                            </select>
                                        </div>

                                        <div v-if="documentData[docType.value].identificationType">
                                            <label class="block mb-1 text-sm font-medium text-gray-900 dark:text-gray-300">
                                                Número do Documento (Opcional)
                                            </label>
                                            <input
                                                v-model="documentData[docType.value].documentNumber"
                                                type="text"
                                                placeholder="Digite o número"
                                                class="block w-full p-2 text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                            />
                                        </div>
                                    </div>

                                    <file-pond
                                        :name="`files-${docType.value}`"
                                        :ref="`pond-${docType.value}`"
                                        label-idle="Arraste e solte arquivos aqui ou <span class='filepond--label-action'>Procure</span>"
                                        :allow-multiple="true"
                                        accepted-file-types="image/jpeg, image/png, image/jpg, application/pdf"
                                        :files="documentData[docType.value].files"
                                        :server="{
                                            process: (fieldName, file, metadata, load, error, progress, abort) => 
                                                uploadFile(docType.value, fieldName, file, metadata, load, error, progress, abort),
                                        }"
                                    />
                                    
                                    <p
                                        v-if="documentData[docType.value].uploadedFiles.length > 0"
                                        class="mt-2 text-sm text-green-600 dark:text-green-400"
                                    >
                                        ✓ {{ documentData[docType.value].uploadedFiles.length }} arquivo(s) pronto(s)
                                    </p>
                                </div>
                            </div>

                            <div class="flex justify-end gap-2 mt-4">
                                <button
                                    type="button"
                                    class="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                                    @click.stop="closeModal"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    :disabled="loading || !canSubmit"
                                    class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    @click="submitUpload"
                                >
                                    <svg
                                        v-if="loading"
                                        aria-hidden="true"
                                        class="w-5 h-5 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                                        viewBox="0 0 100 101"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                            fill="currentFill"
                                        />
                                    </svg>
                                    {{ loading ? "Enviando..." : "Fazer Upload" }}
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogPanel,
    DialogTitle,
} from "@headlessui/vue";
import vueFilePond from "vue-filepond";
import "filepond/dist/filepond.min.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import { useToast } from "vue-toastification";

const FilePond = vueFilePond(FilePondPluginFileValidateType);

export default {
    components: {
        TransitionRoot,
        TransitionChild,
        Dialog,
        DialogPanel,
        DialogTitle,
        FilePond,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        userId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            loadingDocuments: false,
            existingDocuments: [],
            user: null,
            documentData: {},
            isClosing: false,
        };
    },
    computed: {
        documentTypes() {
            const types = [
                { value: "selfie_photo", label: "Foto de Selfie" },
                { value: "document_front", label: "Documento Frente" },
                { value: "document_back", label: "Documento Verso" },
            ];

            if (this.isMinor) {
                types.push({ value: "emancipated_document", label: "Documento de Emancipação" });
            }

            return types;
        },
        isMinor() {
            if (!this.user?.birth) {
                return false;
            }

            const birthParts = this.user.birth.split('/');
            const birthDate = new Date(
                parseInt(birthParts[2]),
                parseInt(birthParts[1]) - 1,
                parseInt(birthParts[0])
            );
            
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }

            return age < 18;
        },
        pendingDocuments() {
            const existingTypes = this.existingDocuments.map(doc => doc.collection_name);
            return this.documentTypes.filter(
                docType => !existingTypes.includes(docType.value)
            );
        },
        canSubmit() {
            return Object.values(this.documentData).some(data => 
                data.uploadedFiles.length > 0 &&
                (!this.needsIdentificationType(data.collection) || data.identificationType)
            );
        },
        totalFilesToUpload() {
            return Object.values(this.documentData).reduce(
                (total, data) => total + data.uploadedFiles.length,
                0
            );
        },
    },
    watch: {
        isOpen(newValue) {
            if (newValue) {
                this.isClosing = false;
                this.fetchExistingDocuments();
            }
        },
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    methods: {
        needsIdentificationType(collection) {
            return collection === 'document_front' || collection === 'document_back';
        },
        async fetchExistingDocuments() {
            this.loadingDocuments = true;
            try {
                const [documentsResponse, userResponse] = await Promise.all([
                    axios.get(`/api/user/${this.userId}/documents`),
                    axios.get(`/api/user/${this.userId}`)
                ]);
                
                this.existingDocuments = documentsResponse.data;
                this.user = userResponse.data;
                this.initializeDocumentData();
            } catch (error) {
                console.error('Erro ao buscar documentos:', error);
                this.toast.error('Erro ao carregar documentos existentes');
            } finally {
                this.loadingDocuments = false;
            }
        },
        initializeDocumentData() {
            this.documentData = {};
            this.pendingDocuments.forEach(docType => {
                this.documentData[docType.value] = {
                    collection: docType.value,
                    identificationType: '',
                    documentNumber: '',
                    files: [],
                    uploadedFiles: [],
                };
            });
        },
        closeModal() {
            if (!this.loading && !this.isClosing) {
                this.isClosing = true;
                this.resetForm();
                this.$emit("close");
            }
        },
        resetForm() {
            this.existingDocuments = [];
            this.documentData = {};
        },
        async store(file, options = null) {
            const response = await axios.post(
                "/vapor/signed-storage-url",
                {
                    bucket: options.bucket || "",
                    content_type: options.contentType || file.type,
                    expires: options.expires || "",
                    visibility: "public-read",
                    ...options.data,
                },
                {
                    ...options.options,
                }
            );

            if (response.status !== 200 && response.status !== 201) {
                throw new Error("Erro ao fazer upload.");
            }

            let headers = response.data.headers;

            if ("Host" in headers) {
                delete headers.Host;
            }

            if (typeof options.progress === "undefined") {
                options.progress = () => {};
            }

            const cancelToken = options.cancelToken || "";

            await axios.put(response.data.url, file, {
                cancelToken: cancelToken,
                headers: headers,
                withCredentials: false,
                onUploadProgress: (progressEvent) => {
                    options.progress(
                        progressEvent.loaded / progressEvent.total
                    );
                },
            });

            response.data.extension = file.name.split(".").pop();

            return response.data;
        },
        uploadFile(collection, fieldName, file, metadata, load, error, filePondProgress, abort) {
            const allowedTypes = [
                "image/jpeg",
                "image/png",
                "image/jpg",
                "application/pdf",
            ];
            const allowedExtensions = [".jpg", ".jpeg", ".png", ".pdf"];
            const fileName = file.name.toLowerCase();
            const fileExtension = fileName.substring(fileName.lastIndexOf("."));

            const isValidType =
                allowedTypes.includes(file.type) ||
                allowedExtensions.includes(fileExtension);

            if (!isValidType) {
                this.toast.error(
                    "Tipo de arquivo inválido. Use imagens (JPG, PNG) ou PDF."
                );
                abort();
                return;
            }

            this.store(file, {
                progress: async (progressValue) => {
                    filePondProgress(Math.round(progressValue * 100));
                },
                abort,
            })
                .then((response) => {
                    load(response);
                    this.documentData[collection].uploadedFiles.push({
                        key: response?.key,
                        name: file.name,
                        type: file.type,
                    });
                })
                .catch((err) => {
                    console.error("Erro ao fazer upload do arquivo:", err);
                    error("Erro ao fazer upload do arquivo");
                    abort();
                });

            return {
                abort: () => {
                    abort();
                },
            };
        },
        async submitUpload() {
            if (!this.canSubmit) {
                return;
            }

            this.loading = true;

            try {
                const uploadPromises = [];
                
                for (const [collection, data] of Object.entries(this.documentData)) {
                    if (data.uploadedFiles.length > 0) {
                        const needsType = this.needsIdentificationType(collection);
                        
                        if (needsType && !data.identificationType) {
                            this.toast.error(`Selecione o tipo de identificação para ${this.documentTypes.find(d => d.value === collection)?.label}`);
                            this.loading = false;
                            return;
                        }

                        uploadPromises.push(
                            axios.post(`/api/user/${this.userId}/documents/upload`, {
                                collection: collection,
                                files: data.uploadedFiles.map(f => f.key),
                                document_type: data.identificationType || null,
                                document_number: data.documentNumber || null,
                            })
                        );
                    }
                }

                await Promise.all(uploadPromises);

                this.toast.success(`${this.totalFilesToUpload} documento(s) enviado(s) com sucesso!`);
                
                // Recarregar a página após sucesso
                window.location.reload();
            } catch (err) {
                console.error("Erro ao fazer upload:", err);
                this.toast.error(
                    err.response?.data?.message ||
                        "Erro ao fazer upload dos documentos"
                );
                this.loading = false;
            }
        },
    },
};
</script>
